export class WorkRequest {
  constructor({
    id = null,
    assignedServiceId = null,
    createdAt = null,
    createdBy = null,
    description = '',
    location = {
      building: '',
      floor: '',
      room: ''
    },
    priority = 'medium',
    problem = '',
    requestingServiceId = null,
    schoolId = null,
    status = 'pending'
  } = {}) {
    this.id = id;
    this.assignedServiceId = assignedServiceId;
    this.createdAt = createdAt;
    this.createdBy = createdBy;
    this.description = description;
    this.location = location;
    this.priority = priority;
    this.problem = problem;
    this.requestingServiceId = requestingServiceId;
    this.schoolId = schoolId;
    this.status = status;
  }

  static fromFirestore(doc) {
    const data = doc.data();
    return new WorkRequest({
      id: doc.id,
      assignedServiceId: data.assignedServiceId,
      createdAt: data.createdAt?.toDate(),
      createdBy: data.createdBy,
      description: data.description,
      location: {
        building: data.location?.building || '',
        floor: data.location?.floor || '',
        room: data.location?.room || ''
      },
      priority: data.priority,
      problem: data.problem,
      requestingServiceId: data.requestingServiceId,
      schoolId: data.schoolId,
      status: data.status
    });
  }

  toFirestore() {
    return {
      assignedServiceId: this.assignedServiceId,
      createdAt: this.createdAt,
      createdBy: this.createdBy,
      description: this.description,
      location: this.location,
      priority: this.priority,
      problem: this.problem,
      requestingServiceId: this.requestingServiceId,
      schoolId: this.schoolId,
      status: this.status
    };
  }

  static get PRIORITY() {
    return {
      LOW: 'low',
      MEDIUM: 'medium',
      HIGH: 'high',
      URGENT: 'urgent'
    };
  }

  static get STATUS() {
    return {
      PENDING: 'pending',
      IN_PROGRESS: 'in_progress',
      COMPLETED: 'completed',
      CANCELLED: 'cancelled'
    };
  }
}

export const PRIORITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

export const STATUS_TYPES = {
  PENDING_VALIDATION: 'pending_validation',
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

export const STATUS_LABELS = {
  pending_validation: 'En attente de validation',
  pending: 'En attente',
  in_progress: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé',
};

export const STATUS_COLORS = {
  pending_validation: 'warning',
  pending: 'default',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'error',
};

export const normalizeStatus = (status) => {
  if (!status) return STATUS_TYPES.PENDING;
  
  const normalized = status.toLowerCase();
  switch (normalized) {
    case 'pending_validation':
      return STATUS_TYPES.PENDING_VALIDATION;
    case 'pending':
    case 'en_attente':
      return STATUS_TYPES.PENDING;
    case 'in_progress':
    case 'en_cours':
      return STATUS_TYPES.IN_PROGRESS;
    case 'completed':
    case 'termine':
      return STATUS_TYPES.COMPLETED;
    case 'cancelled':
    case 'annule':
      return STATUS_TYPES.CANCELLED;
    default:
      return STATUS_TYPES.PENDING;
  }
};

// Fonction pour normaliser les priorités
export const normalizePriority = (priority) => {
  if (!priority) return PRIORITY_LEVELS.MEDIUM;
  
  const normalized = priority.toLowerCase();
  switch (normalized) {
    case 'low':
    case 'basse':
      return PRIORITY_LEVELS.LOW;
    case 'medium':
    case 'moyenne':
      return PRIORITY_LEVELS.MEDIUM;
    case 'high':
    case 'haute':
      return PRIORITY_LEVELS.HIGH;
    case 'urgent':
      return PRIORITY_LEVELS.URGENT;
    default:
      return PRIORITY_LEVELS.MEDIUM;
  }
};

export const priorityColors = {
  [PRIORITY_LEVELS.LOW]: '#4caf50',
  [PRIORITY_LEVELS.MEDIUM]: '#ff9800',
  [PRIORITY_LEVELS.HIGH]: '#f44336',
  [PRIORITY_LEVELS.URGENT]: '#d32f2f'
};

export const statusColors = {
  [STATUS_TYPES.PENDING]: '#ffd700',
  [STATUS_TYPES.IN_PROGRESS]: '#1976d2',
  [STATUS_TYPES.COMPLETED]: '#4caf50',
  [STATUS_TYPES.CANCELLED]: '#9e9e9e'
};

// Types de problèmes avec leurs icônes
export const PROBLEM_TYPES = {
  BROKEN: 'broken',
  LEAK: 'leak',
  ELECTRICAL: 'electrical',
  HEATING: 'heating',
  CLEANING: 'cleaning',
  PAINTING: 'painting',
  FURNITURE: 'furniture',
  SECURITY: 'security',
  NETWORK: 'network',
  OTHER: 'other'
};

// Traduction des problèmes
export const PROBLEM_TRANSLATIONS = {
  [PROBLEM_TYPES.BROKEN]: 'Problème de fonctionnement',
  [PROBLEM_TYPES.LEAK]: 'Fuite d\'eau',
  [PROBLEM_TYPES.ELECTRICAL]: 'Problème électrique',
  [PROBLEM_TYPES.HEATING]: 'Chauffage/Climatisation',
  [PROBLEM_TYPES.CLEANING]: 'Nettoyage',
  [PROBLEM_TYPES.PAINTING]: 'Peinture/Rénovation',
  [PROBLEM_TYPES.FURNITURE]: 'Mobilier',
  [PROBLEM_TYPES.SECURITY]: 'Sécurité',
  [PROBLEM_TYPES.NETWORK]: 'Réseau/Internet',
  [PROBLEM_TYPES.OTHER]: 'Autre'
};

// Icônes des problèmes
export const PROBLEM_ICONS = {
  [PROBLEM_TYPES.BROKEN]: 'BuildIcon',
  [PROBLEM_TYPES.LEAK]: 'OpacityIcon',
  [PROBLEM_TYPES.ELECTRICAL]: 'ElectricBoltIcon',
  [PROBLEM_TYPES.HEATING]: 'ThermostatIcon',
  [PROBLEM_TYPES.CLEANING]: 'CleaningServicesIcon',
  [PROBLEM_TYPES.PAINTING]: 'FormatPaintIcon',
  [PROBLEM_TYPES.FURNITURE]: 'ChairIcon',
  [PROBLEM_TYPES.SECURITY]: 'SecurityIcon',
  [PROBLEM_TYPES.NETWORK]: 'WifiIcon',
  [PROBLEM_TYPES.OTHER]: 'MoreHorizIcon'
};

// Bâtiments disponibles
export const BUILDINGS = {
  MAIN: 'batiment_principal',
  ADMIN: 'administration',
  GYM: 'gymnase',
  SCIENCE: 'batiment_sciences',
  TECH: 'batiment_technologie',
  LIBRARY: 'bibliotheque',
  CAFETERIA: 'cafeteria',
  WORKSHOP: 'atelier',
  DORM: 'internat',
  SPORTS: 'complexe_sportif',
  KITCHEN: 'cuisine',
  LAUNDRY: 'lingerie',
  INFIRMARY: 'infirmerie',
  OUTDOOR: 'espace_exterieur'
};

// Traduction des bâtiments
export const BUILDING_TRANSLATIONS = {
  [BUILDINGS.MAIN]: 'Bâtiment Principal',
  [BUILDINGS.ADMIN]: 'Administration',
  [BUILDINGS.GYM]: 'Gymnase',
  [BUILDINGS.SCIENCE]: 'Bâtiment Sciences',
  [BUILDINGS.TECH]: 'Bâtiment Technologie',
  [BUILDINGS.LIBRARY]: 'Bibliothèque',
  [BUILDINGS.CAFETERIA]: 'Cafétéria',
  [BUILDINGS.WORKSHOP]: 'Atelier',
  [BUILDINGS.DORM]: 'Internat',
  [BUILDINGS.SPORTS]: 'Complexe Sportif',
  [BUILDINGS.KITCHEN]: 'Cuisine',
  [BUILDINGS.LAUNDRY]: 'Lingerie',
  [BUILDINGS.INFIRMARY]: 'Infirmerie',
  [BUILDINGS.OUTDOOR]: 'Espace Extérieur'
};

// Icônes des bâtiments
export const BUILDING_ICONS = {
  [BUILDINGS.MAIN]: 'Business',
  [BUILDINGS.ADMIN]: 'AdminPanelSettings',
  [BUILDINGS.GYM]: 'FitnessCenter',
  [BUILDINGS.SCIENCE]: 'Science',
  [BUILDINGS.TECH]: 'Computer',
  [BUILDINGS.LIBRARY]: 'LocalLibrary',
  [BUILDINGS.CAFETERIA]: 'Restaurant',
  [BUILDINGS.WORKSHOP]: 'Construction',
  [BUILDINGS.DORM]: 'Hotel',
  [BUILDINGS.SPORTS]: 'Sports',
  [BUILDINGS.KITCHEN]: 'Kitchen',
  [BUILDINGS.LAUNDRY]: 'LocalLaundryService',
  [BUILDINGS.INFIRMARY]: 'LocalHospital',
  [BUILDINGS.OUTDOOR]: 'Terrain'
};

// Étages disponibles
export const FLOORS = {
  BASEMENT: 'sous_sol',
  GROUND: 'rdc',
  FIRST: 'premier',
  SECOND: 'deuxieme',
  THIRD: 'troisieme',
  FOURTH: 'quatrieme'
};

// Traduction des étages
export const FLOOR_TRANSLATIONS = {
  [FLOORS.BASEMENT]: 'Sous-sol',
  [FLOORS.GROUND]: 'Rez-de-chaussée',
  [FLOORS.FIRST]: '1er étage',
  [FLOORS.SECOND]: '2ème étage',
  [FLOORS.THIRD]: '3ème étage',
  [FLOORS.FOURTH]: '4ème étage'
};
